import React, { createContext, useContext, useEffect } from 'react';
import {
  sendTransfer,
  sendMethod,
  sendDvdInitiate,
  sendDvdApprove,
  sendDvdCancel,
} from '../components/trexApi';

import { getBalanceERC20 } from '../components/ethereumApi';
import { getAccount, watchAccount, getNetwork, watchNetwork, switchNetwork } from '@wagmi/core';
import { BigNumber } from 'bignumber.js';
import store from 'app/state/store';
import { ethereumNetworkInfo, toast } from '@tokenyico/design-system';
import { connect } from 'redux-zero/react';
import { chainList as chains } from '../../index';

export const ClientContext = createContext(null);

/**
 * Helpers
 */
const handleAccountChange = (address) => {
  const {
    holder: { selectedToken, balances },
  } = store.getState();
  const sender = balances?.find((balance) => balance.token.id === selectedToken.id)?.holder.wallet;

  if (address && balances.length) {
    if (address.toLowerCase() !== sender?.toLowerCase()) {
      const message = 'Connected Wallet does not match!';
      const walletconnect = localStorage.getItem('walletconnect');
      if (walletconnect) {
        localStorage.removeItem('walletconnect');
      }
      return message;
    }
  }
};

export const convertStringToBN = (amount, decimals) =>
  new BigNumber(amount).multipliedBy(Math.pow(10, decimals)).toFixed(0);

export const convertBNtoString = (amount, decimals) =>
  new BigNumber(amount).dividedBy(Math.pow(10, decimals)).toString();

/**
 * ClientContext
 */
const ClientContextProvider = ({ children, selectedToken, user }) => {
  const getNetworkId = (network) => {
    if (!network) {
      return undefined;
    }
    return ethereumNetworkInfo.get(network).chainId;
  };

  const networkName = selectedToken?.blockchainNetwork;
  const networkId = getNetworkId(networkName);
  const defaultChain = chains.find((chain) => chain.id === networkId);

  /**
   * Watch events
   */
  useEffect(() => {
    const unwatchNetwork = watchNetwork(({ chain }) => {
      if (!user?.id) return; // if user is not logged in, don't watch the network
      if (chain && Number(networkId) !== Number(chain?.id)) {
        toast.error(
          `Connected Ethereum Network does not match! Please change your network to ${networkName.toUpperCase()}`,
        );
      }
    });
    const unwatchAccount = watchAccount(({ address }) => {
      const message = handleAccountChange(address);
      if (message) toast.error('Error', message);
    });

    return () => {
      unwatchNetwork();
      unwatchAccount();
    };
  }, [networkName, networkId, selectedToken]);

  const callContract = async (
    functionSig,
    { tokenAddress, toAddress, amount, decimals, ...otherData },
  ) => {
    const { address } = getAccount();
    const { chain } = getNetwork();

    // force to switch network if default network is not selected
    if (Number(networkId) !== Number(chain?.id) && chain) {
      switchNetwork({ chainId: defaultChain.id });
      throw new Error(
        `Connected Ethereum Network does not match! Please change your network to ${networkName.toUpperCase()}`,
      );
    }

    // check if agent wallet is connected
    const message = handleAccountChange(address);
    if (message) throw new Error(message);

    let result;
    if (functionSig === 'transfer') {
      // send transaction
      result = await sendTransfer({
        tokenAddress,
        // use metamask wallet
        fromAddress: address,
        toAddress,
        amount,
        decimals,
        network: networkName,
        data: otherData,
      });
    }

    if (['decreaseAllowance', 'increaseAllowance', 'approve'].includes(functionSig)) {
      // send transaction
      result = await sendMethod({
        method: functionSig,
        tokenAddress,
        // use metamask wallet
        fromAddress: address,
        toAddress,
        amount,
        decimals,
      });
    }

    if (functionSig === 'initiateDVDTransfer') {
      result = await sendDvdInitiate({
        fromAddress: address, // use metamask wallet
        network: networkName.toLowerCase(),
        data: otherData,
      });
    }

    if (functionSig === 'approveDVDTransfer') {
      result = await sendDvdApprove({
        fromAddress: address, // use metamask wallet
        network: networkName.toLowerCase(),
        data: otherData,
      });
    }

    if (functionSig === 'cancelDVDTransfer') {
      result = await sendDvdCancel({
        fromAddress: address, // use metamask wallet
        network: networkName.toLowerCase(),
        data: otherData,
      });
    }

    if (functionSig === 'balance') {
      result = await getBalanceERC20({
        tokenAddress,
        walletAddress: address,
      });
    }

    // format displayed result
    const formattedResult = {
      action: functionSig,
      result,
    };

    // display result
    return formattedResult;
  };

  const value = {
    methods: {
      callContract,
    },
  };

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
};

export default connect((state) => state.holder)(ClientContextProvider);

export const useClientContext = () => {
  return useContext(ClientContext);
};
